import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  MatAccordion,
  MatExpansionModule,
  MatExpansionPanel,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { connectState } from '@examdojo/angular/util';
import { getAnimationAppearDisappearWithHeight } from '@examdojo/animation';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { ToastService } from '@examdojo/core/toast';
import { isNotNullish, isNullish } from '@examdojo/core/util/nullish';
import {
  ActiveMarkschemeEditorItemCoordinates,
  ActiveMarkschemeEditorSubItemCoordinates,
  createSolutionPathFormGroup,
  isActiveMarkschemeEditorItem,
  MarkschemeEditorComponent,
} from '@examdojo/markscheme-v2';
import { ContentItemTypeEnum, ImageContentItemFormGroup } from '@examdojo/models/content-item';
import { MarkCalculationMethod } from '@examdojo/models/markscheme-v2';
import { QuestionItemTypeEnum, StemCoordinates } from '@examdojo/models/question';
import { ContentItemFormGroup, TableContentItemFormGroup } from '@examdojo/question-content-item';
import { catchError, combineLatest, finalize, map, of, Subject, switchMap, tap } from 'rxjs';
import { getMarksFromStem } from '../../models/question.model';
import { ActiveQuestionEditorItem, StemFormGroup } from '../question-item-editor.model';
import { QuestionItemsFormService } from '../question-items-form.service';
import { StemLabelComponent } from '../stem-label/stem-label.component';
import { QuestionItemStemContentViewerComponent } from './question-item-stem-content-viewer.component';

@Component({
  selector: 'dojo-question-item-editor-stem-viewer',
  imports: [
    QuestionItemStemContentViewerComponent,
    StemLabelComponent,
    MarkschemeEditorComponent,
    ReactiveFormsModule,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelTitle,
    MatExpansionModule,
    ButtonComponent,
  ],
  template: `
    @if (state.editingOptions) {
      @if (stemFormGroup(); as stem) {
        <dojo-question-item-stem-content-viewer
          [contentItemsFormArray]="stem.controls.content_items"
          [isAllowedToEditImage]="state.editingOptions.allowImageEditing.length > 0"
          [isAllowedToEditTable]="
            state.editingOptions.allowImageEditing.length > 0 || state.editingOptions.allowQuestionEditing
          "
          (editItem)="editItem($event)"
          [showSelectorBadge]="!state.editingOptions.hideBadges || state.editingOptions.allowQuestionEditing"
          [isActiveSelectorBadge]="activeQuestionItem()?.ctrl === stem"
          [isSelectorBadgeDisabled]="isSelectingDisabled() || !state.editingOptions.allowQuestionEditing"
          [stemTotalMarks]="stemTotalMarks()"
          (selectorBadgeClicked)="
            setActiveQuestionItem({
              item: { ctrl: stem, type: QuestionItemTypeEnum.Stem }
            })
          "
        />

        @if (stem.controls.markscheme.value !== null && !state.editingOptions.hideMarkscheme) {
          <mat-accordion class="custom-mat-accordion mt-2 w-full" multi>
            <mat-expansion-panel
              [expanded]="openedMarkschemeItemId() === stemIndex()"
              (opened)="openedMarkschemeItemId.set(stemIndex())"
              (closed)="openedMarkschemeItemId.set(null)"
            >
              <mat-expansion-panel-header>
                <mat-panel-title class="text-primary font-semibold-exam flex items-center gap-1 text-sm">
                  Markscheme
                  <dojo-stem-label />
                </mat-panel-title>
              </mat-expansion-panel-header>

              @if (isGeneratingSolutionPath()) {
                <div class="z-3 flex h-full w-full flex-col items-center justify-center gap-2 bg-neutral-50">
                  <span>Generating solution paths...</span>
                  <dojo-button [size]="'xs'" [fill]="'outline'" (click)="cancelGeneratingSolutionPath()">
                    Cancel
                  </dojo-button>
                </div>
              }

              <dojo-markscheme-editor
                class="cursor-default"
                @appearDisappearWithHeight
                [formCtrl]="$any(stem.controls).markscheme"
                [disabled]="isSelectingDisabled() || !state.editingOptions.allowMarkschemeEditing"
                [editingOptions]="state.editingOptions"
                [activeFormGroupItem]="
                  activeQuestionItemType() && isActiveMarkschemeEditorItem(activeQuestionItemType())
                    ? $any(activeQuestionItem())
                    : null
                "
                [allowSolutionPathGeneration]="state.allowSolutionPathGeneration"
                (setActiveFormGroupItem)="setActiveQuestionItem($event)"
                (generateSolutionPaths)="generateSolutionPath()"
                (editItem)="editItem($event, false)"
              />
            </mat-expansion-panel>
          </mat-accordion>
        }
      }
    }
  `,
  styleUrl: './question-item-editor-stem-viewer.component.scss',
  animations: [getAnimationAppearDisappearWithHeight()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionItemEditorStemViewerComponent {
  constructor(
    private readonly questionItemsFormService: QuestionItemsFormService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly toastService: ToastService,
    private readonly destroyRef: DestroyRef,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.listenForQuestionIdForSolutionPathGeneration().pipe(takeUntilDestroyed()).subscribe();

    effect(() => {
      this.stemTotalMarks.set(this.calculateStemTotalMarks());
    });
  }

  readonly stemFormGroup = input.required<StemFormGroup>();
  readonly stemIndex = input.required<number>();
  readonly stemGroupIndex = input<number | null>(null);
  readonly markCalculationMethod = input.required<MarkCalculationMethod>();

  readonly stemCoordinates = computed<StemCoordinates>(() => {
    const stemGroupIndex = this.stemGroupIndex();
    const stemIndex = this.stemIndex();

    if (isNullish(stemGroupIndex)) {
      return { rootItemIndex: stemIndex };
    }

    return {
      rootItemIndex: stemGroupIndex,
      stems: stemIndex,
    };
  });

  readonly isSelectingDisabled = input.required<boolean>();

  readonly activeQuestionItem = this.questionItemsFormService.activeQuestionItem;
  readonly activeQuestionItemType = this.questionItemsFormService.activeQuestionItemType;
  readonly openedMarkschemeItemId = signal<number | null>(null);

  readonly isGeneratingSolutionPath = signal(false);

  readonly QuestionItemTypeEnum = QuestionItemTypeEnum;

  readonly isActiveMarkschemeEditorItem = isActiveMarkschemeEditorItem;

  private readonly generateSolutionPathTrigger$$ = new Subject<boolean>();

  private readonly editingOptions$ = this.questionItemsFormService.editingOptions$;

  readonly state = connectState({
    markGroupWasChanged: of(true),
    allowSolutionPathGeneration: combineLatest([
      this.questionItemsFormService.generateSolutionQuestionId$.pipe(map((id) => id !== null)),
      this.editingOptions$.pipe(map((options) => options?.allowMarkschemeStructureEditing ?? false)),
    ]).pipe(
      map(
        ([generateSolutionQuestionId, allowMarkschemeStructureEditing]) =>
          generateSolutionQuestionId && allowMarkschemeStructureEditing,
      ),
    ),
    editingOptions: this.editingOptions$,
  });

  readonly stemTotalMarks = signal(0);

  setActiveQuestionItem({
    item,
    coordinates,
  }: {
    item: ActiveQuestionEditorItem;
    coordinates?: ActiveMarkschemeEditorItemCoordinates;
  }) {
    this.questionItemsFormService.setActiveQuestionItem(item, {
      ...this.stemCoordinates(),
      ...(coordinates ?? {}),
    });
  }

  toggleMarkschemeResult(index: number) {
    if (this.openedMarkschemeItemId() === index) {
      this.openedMarkschemeItemId.set(null);
    } else {
      this.openedMarkschemeItemId.set(index);
    }
  }

  generateSolutionPath() {
    this.generateSolutionPathTrigger$$.next(true);
  }

  cancelGeneratingSolutionPath() {
    this.generateSolutionPathTrigger$$.next(false);
  }

  editItem(
    {
      item,
      ...coordinateInfo
    }:
      | {
          item: ImageContentItemFormGroup | TableContentItemFormGroup;
          coordinates: ActiveMarkschemeEditorSubItemCoordinates;
        }
      | {
          item: ContentItemFormGroup;
          contentItemIndex: number;
        },
    setStemAsActive = true,
  ) {
    if (item.value.type !== ContentItemTypeEnum.Image && item.value.type !== ContentItemTypeEnum.Table) {
      return;
    }

    const currentActiveItem = this.activeQuestionItem();
    if (setStemAsActive && currentActiveItem?.ctrl !== this.stemFormGroup()) {
      this.questionItemsFormService.setActiveQuestionItem(
        {
          ctrl: this.stemFormGroup(),
          type: QuestionItemTypeEnum.Stem,
        },
        this.stemCoordinates(),
      );
    }

    const subItemCoordinates =
      'contentItemIndex' in coordinateInfo
        ? { content_items: coordinateInfo.contentItemIndex }
        : coordinateInfo.coordinates;
    if (item.value.type === ContentItemTypeEnum.Image) {
      this.questionItemsFormService.setActiveQuestionSubItem(item as ImageContentItemFormGroup, subItemCoordinates);
    } else if (item.value.type === ContentItemTypeEnum.Table) {
      this.questionItemsFormService.setActiveQuestionSubItem(item as TableContentItemFormGroup, subItemCoordinates);
    }
  }

  private calculateStemTotalMarks() {
    const stemValue = this.stemFormGroup().getRawValue();
    return stemValue.markscheme ? getMarksFromStem(stemValue, this.markCalculationMethod()) : 0;
  }

  private listenForQuestionIdForSolutionPathGeneration() {
    return this.questionItemsFormService.generateSolutionQuestionId$.pipe(
      switchMap((generateSolutionQuestionId) =>
        isNotNullish(generateSolutionQuestionId)
          ? this.listenForGenerateSolutionPathTrigger(generateSolutionQuestionId)
          : of(null),
      ),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  private listenForGenerateSolutionPathTrigger(generateSolutionQuestionId: number) {
    return this.generateSolutionPathTrigger$$.pipe(
      switchMap((shouldGenerateSolutionPath) => {
        if (!shouldGenerateSolutionPath) {
          return of(null);
        }

        const stemCoordinates: [number | null, number] = [this.stemGroupIndex() ?? null, this.stemIndex()];

        this.isGeneratingSolutionPath.set(true);

        return this.generateSolutionPathByStemCoordinates(stemCoordinates, generateSolutionQuestionId).pipe(
          finalize(() => {
            this.isGeneratingSolutionPath.set(false);
          }),
        );
      }),
      catchError(() => {
        this.toastService.error('Could not trigger solution path generation. Please contact support.');
        return of(null);
      }),
      finalize(() => {
        this.isGeneratingSolutionPath.set(false);
      }),
    );
  }

  private generateSolutionPathByStemCoordinates(
    stemCoordinates: [number | null, number],
    generateSolutionQuestionId: number,
  ) {
    return this.questionItemsFormService.generateSolutionPathForStem(stemCoordinates, generateSolutionQuestionId).pipe(
      tap((result) => {
        this.isGeneratingSolutionPath.set(false);
        const editingOptions = this.state.editingOptions;

        if (!result || !editingOptions) {
          return;
        }

        const markschemeControl = this.stemFormGroup().controls.markscheme;

        if (result && markschemeControl.value) {
          if (markschemeControl.controls.solution_paths.length > result.length) {
            // if regenerated solution path do not have existing solution paths, remove the excess solution paths, before setting the values
            const excessSolutionPathsNumber = markschemeControl.controls.solution_paths.length - result.length;

            for (let i = 0; i < excessSolutionPathsNumber; i++) {
              markschemeControl.controls.solution_paths.removeAt(markschemeControl.controls.solution_paths.length - 1);
            }
          }

          result.forEach((solutionPath, index) => {
            const solutionPathControl = markschemeControl.controls.solution_paths.controls[index];
            if (solutionPathControl) {
              solutionPathControl.controls.mark_group_refs.setValue(solutionPath.mark_group_refs);
              return;
            }

            const newSolutionPathControl = createSolutionPathFormGroup(solutionPath, editingOptions);

            markschemeControl.controls.solution_paths.push(newSolutionPathControl);
          });

          this.stemTotalMarks.set(this.calculateStemTotalMarks());
          this.cdr.detectChanges();
          this.toastService.success('Solution paths generated successfully');
        }
      }),
      this.errorHandlerService.catchError(
        '[QuestionItemEditorStemViewerComponent]: could not generate solution path',
        () => {
          return of(null);
        },
        {
          toast: (err) => ({
            title: 'Could not generate solution paths',
            description: (err as Error)?.message,
          }),
        },
      ),
    );
  }
}
